import { api, createQueryHandler } from './base';
import {
  CreateBlindStructureDto,
  GetTournamentCurrencyLedgerResponse,
  GetTournamentsInfoResp,
  Tournament,
  TournamentRecurringScheduleDto,
  TournamentTicketDto,
  UpdateBlindStructureDto,
  AdminGetTournamentsDto,
  TournamentStatus,
  TournamentSortBy,
  Prisma,
} from '@repo/common-types';
import { cancelTournamentRest, cancelTournamentRestLobby } from '@/services/tournaments/tournaments-service';
// Custom hooks that integrate with Redux state
import { useSelector } from 'react-redux';
import { AppState } from '@/redux/root';

export const tournamentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTournaments: builder.query<GetTournamentsInfoResp, AdminGetTournamentsDto | void>({
      query: (params) => {
        return createQueryHandler(
          '/v1/admin/tournaments',
          'GET',
        )({
          query: params ?? {},
        });
      },
      providesTags: ['Tournament'],
    }),
    getEligibleTargetTournaments: builder.query<GetTournamentsInfoResp, void>({
      query: () => {
        const now = new Date().toISOString();
        return createQueryHandler(
          '/v1/admin/tournaments',
          'GET',
        )({
          query: {
            skip: 0,
            take: 1000,
            filters: {
              status: [TournamentStatus.CREATED],
              startTime: {
                from: now,
              },
            },
            sortBy: TournamentSortBy.START_TIME,
            sortOrder: Prisma.SortOrder.asc,
          } satisfies AdminGetTournamentsDto,
        });
      },
      providesTags: ['Tournament'],
    }),
    createTournament: builder.mutation({
      query: createQueryHandler('/v1/admin/tournaments/templates/:templateId', 'POST'),
      invalidatesTags: ['Tournament'],
    }),
    updateTournament: builder.mutation<Tournament, { id: string } & Partial<Tournament>>({
      query: ({ id, ...body }) => {
        const handler = createQueryHandler(`/v1/admin/tournaments/${id}`, 'PUT');
        return handler(body);
      },
      invalidatesTags: ['Tournament'],
    }),
    deleteTournament: builder.mutation<void, string>({
      query: (id) => {
        const handler = createQueryHandler(`/v1/admin/tournaments/${id}`, 'DELETE');
        return handler({});
      },
      invalidatesTags: ['Tournament'],
    }),
    getTournamentSchedules: builder.query<TournamentRecurringScheduleDto[], void>({
      query: createQueryHandler('/v1/admin/tournaments/recurring-schedules', 'GET'),
      providesTags: ['TournamentSchedule'],
    }),
    updateTournamentSchedule: builder.mutation<
      TournamentRecurringScheduleDto,
      { id: string } & Partial<TournamentRecurringScheduleDto>
    >({
      query: ({ id, ...body }) => {
        const handler = createQueryHandler(`/v1/admin/tournaments/recurring-schedules/${id}`, 'PATCH');
        return handler(body);
      },
      invalidatesTags: ['TournamentSchedule'],
    }),
    cancelTournament: builder.mutation({
      queryFn: async ({ tournamentId, tournamentUrl }) => {
        // If we have a tournament URL, try to cancel via tournament server first
        if (tournamentUrl) {
          try {
            console.log('Attempting to cancel tournament on tournament server:', tournamentUrl);
            const result = await cancelTournamentRest(tournamentUrl);
            console.log('Successfully cancelled on tournament server');
            return { data: result };
          } catch (error) {
            console.warn('Tournament server cancellation failed, falling back to lobby server:', error);
            // Fall through to lobby server
          }
        }

        // Fallback to lobby server (or direct call if no tournament URL)
        try {
          console.log('Cancelling tournament on lobby server');
          const result = await cancelTournamentRestLobby(tournamentId);
          console.log('Successfully cancelled on lobby server');
          return { data: result };
        } catch (error: any) {
          console.error('Failed to cancel tournament on both servers:', error);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: error?.response?.data || { message: 'Failed to cancel tournament' },
              error: error?.message || 'Failed to cancel tournament',
            },
          };
        }
      },
      invalidatesTags: ['Tournament'],
    }),
    updateBlindStructure: builder.mutation<void, UpdateBlindStructureDto>({
      query: ({ id, ...body }) => {
        const handler = createQueryHandler(`/v1/admin/tournaments/blind-structures/${id}`, 'PATCH');
        return handler(body);
      },
      invalidatesTags: ['BlindStructure'],
    }),
    createBlindStructure: builder.mutation<void, CreateBlindStructureDto>({
      query: (body) => {
        const handler = createQueryHandler(`/v1/admin/tournaments/blind-structures`, 'POST');
        return handler(body);
      },
      invalidatesTags: ['BlindStructure'],
    }),
    getUserTickets: builder.query<
      { tickets: TournamentTicketDto[]; total: number; skip: number; take: number; tournamentDollars: number },
      { userId: string; skip?: number; take?: number }
    >({
      query: ({ userId, skip, take }) => {
        const handler = createQueryHandler(`/v1/admin/tournament-tickets/user/${userId}`, 'GET');
        return handler({ query: { skip, take } });
      },
      providesTags: ['TournamentTicket'],
    }),
    addTournamentDollars: builder.mutation<{ tournamentDollars: number }, any>({
      query: (payload) => {
        const handler = createQueryHandler('/v1/admin/tournament-tickets/user/:userId/dollars', 'POST');
        return handler(payload);
      },
      invalidatesTags: (result, error, payload) => [
        { type: 'TournamentTicket', id: payload.userId },
        'TournamentTicket',
      ],
    }),
    createTicketForUser: builder.mutation<any, any>({
      query: (payload) => {
        const handler = createQueryHandler('/v1/admin/tournament-tickets/user/:userId/ticket', 'POST');
        return handler(payload);
      },
      invalidatesTags: (result, error, payload) => [
        { type: 'TournamentTicket', id: payload.userId },
        'TournamentTicket',
      ],
    }),
    getTournamentCurrencyLedger: builder.query<
      GetTournamentCurrencyLedgerResponse,
      { userId: string; skip?: number; take?: number }
    >({
      query: ({ userId, skip, take }) => {
        const handler = createQueryHandler(`/v1/admin/tournament-tickets/user/${userId}/currency-ledger`, 'GET');
        return handler({ query: { skip, take } });
      },
      providesTags: ['TournamentTicket'],
    }),
    getTournamentTickets: builder.query<
      { data: any[]; total: number; skip: number; take: number },
      {
        skip?: number;
        take?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        filters?: {
          displayName?: string;
          userId?: string;
          type?: string[];
          status?: string[];
          templateId?: string;
          tournamentId?: string;
          used?: boolean;
          expired?: boolean;
          expiresAt?: { from?: Date; to?: Date };
        };
      }
    >({
      query: (params) => {
        const handler = createQueryHandler('/v1/admin/tournament-tickets', 'GET');
        return handler({ query: params });
      },
      providesTags: ['TournamentTicket'],
    }),
    updateTournamentTicket: builder.mutation<any, { id: string; data: Prisma.TournamentTicketUpdateInput }>({
      query: ({ id, data }) => {
        const handler = createQueryHandler(`/v1/admin/tournament-tickets/${id}`, 'PUT');
        return handler(data);
      },
      invalidatesTags: ['TournamentTicket'],
    }),
    deleteTournamentTicket: builder.mutation<void, string>({
      query: (id) => {
        const handler = createQueryHandler(`/v1/admin/tournament-tickets/${id}`, 'DELETE');
        return handler({});
      },
      invalidatesTags: ['TournamentTicket'],
    }),
    assignTournamentTicket: builder.mutation<any, { ticketId: string; userId: string }>({
      query: ({ ticketId, userId }) => {
        const handler = createQueryHandler(`/v1/admin/tournament-tickets/${ticketId}/assign`, 'PATCH');
        return handler({ userId });
      },
      invalidatesTags: ['TournamentTicket'],
    }),
    unassignTournamentTicket: builder.mutation<any, string>({
      query: (ticketId) => {
        const handler = createQueryHandler(`/v1/admin/tournament-tickets/${ticketId}/unassign`, 'PATCH');
        return handler({});
      },
      invalidatesTags: ['TournamentTicket'],
    }),
    registerTournamentTicket: builder.mutation<any, string>({
      query: (ticketId) => {
        const handler = createQueryHandler(`/v1/admin/tournaments/tickets/${ticketId}/register`, 'POST');
        return handler({});
      },
      invalidatesTags: ['TournamentTicket'],
    }),
    unregisterTournamentTicket: builder.mutation<any, string>({
      query: (ticketId) => {
        const handler = createQueryHandler(`/v1/admin/tournaments/tickets/${ticketId}/unregister`, 'POST');
        return handler({});
      },
      invalidatesTags: ['TournamentTicket'],
    }),
  }),
});

export const {
  useGetTournamentsQuery,
  useGetEligibleTargetTournamentsQuery,
  useCreateTournamentMutation,
  useUpdateTournamentMutation,
  useDeleteTournamentMutation,
  useGetTournamentSchedulesQuery,
  useUpdateTournamentScheduleMutation,
  useCancelTournamentMutation,
  useGetUserTicketsQuery,
  useAddTournamentDollarsMutation,
  useCreateTicketForUserMutation,
  useGetTournamentCurrencyLedgerQuery,
  useGetTournamentTicketsQuery,
  useUpdateTournamentTicketMutation,
  useDeleteTournamentTicketMutation,
  useAssignTournamentTicketMutation,
  useUnassignTournamentTicketMutation,
  useRegisterTournamentTicketMutation,
  useUnregisterTournamentTicketMutation,
} = tournamentsApi;

interface UseGetTournamentsWithFiltersOptions {
  sng: boolean;
}

export const useGetTournamentsWithFilters = ({ sng }: UseGetTournamentsWithFiltersOptions) => {
  const { skip, take, includeArchived, filters, sortBy, sortOrder } = useSelector(
    (state: AppState) => state.tournamentFilters,
  );

  return useGetTournamentsQuery({
    sng,
    includeArchived,
    skip,
    take,
    filters,
    sortBy,
    sortOrder,
  });
};

export const useGetSngsWithFilters = () => {
  const { skip, take, includeArchived, filters, sortBy, sortOrder } = useSelector(
    (state: AppState) => state.sngFilters,
  );

  return useGetTournamentsQuery({
    sng: true,
    includeArchived,
    skip,
    take,
    filters,
    sortBy,
    sortOrder,
  });
};
